/**
 * tf-theme — Toggle light/dark theme + persist + prefers-color-scheme detection.
 *
 * Sostituisce il pattern inline duplicato:
 *   var saved = localStorage.getItem('tf-theme');
 *   var dark = saved === 'dark' || (!saved && matchMedia('(prefers-color-scheme: dark)').matches);
 *   document.documentElement.setAttribute('data-theme', dark ? 'dark' : 'light');
 *
 * Uso JS:
 *   import { initTheme, toggleTheme, getTheme, setTheme } from './modules/tf-theme';
 *   initTheme();              // applica saved/system theme
 *   toggleTheme();            // toggle light↔dark + persist
 *   getTheme();               // 'light' | 'dark'
 *   setTheme('dark');         // forza tema specifico
 *
 * Window globals:
 *   window.tfTheme.toggle()
 *   window.tfTheme.get()
 *   window.tfTheme.set('dark')
 *
 * Events:
 *   - 'tf-theme-change' su window con detail: { theme: 'light' | 'dark' }
 *
 * @since v9.16 (Sprint 5)
 */

const STORAGE_KEY = 'tf-theme';
const ATTR = 'data-theme';
const EVENT_NAME = 'tf-theme-change';

export type Theme = 'light' | 'dark';

declare global {
  interface Window {
    tfTheme?: {
      get: () => Theme;
      set: (theme: Theme) => void;
      toggle: () => Theme;
      init: () => void;
    };
  }
}

function readStored(): Theme | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === 'light' || v === 'dark' ? v : null;
  } catch {
    return null;
  }
}

function detectSystem(): Theme {
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function applyTheme(theme: Theme): void {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute(ATTR, theme);
  document.documentElement.classList.toggle('dark', theme === 'dark');
  document.documentElement.classList.toggle('light', theme === 'light');
}

function notify(theme: Theme): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: { theme } }));
}

/**
 * Get current theme from DOM.
 */
export function getTheme(): Theme {
  if (typeof document === 'undefined') return 'light';
  const v = document.documentElement.getAttribute(ATTR);
  return v === 'dark' ? 'dark' : 'light';
}

/**
 * Set theme + persist + apply.
 */
export function setTheme(theme: Theme): void {
  applyTheme(theme);
  try {
    localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    // ignore
  }
  notify(theme);
}

/**
 * Toggle current → opposite. Returns new theme.
 */
export function toggleTheme(): Theme {
  const next: Theme = getTheme() === 'dark' ? 'light' : 'dark';
  setTheme(next);
  return next;
}

/**
 * Init: applica saved theme se presente, altrimenti system preference.
 * Idempotente, safe da chiamare multiple volte.
 */
export function initTheme(): void {
  const stored = readStored();
  const theme: Theme = stored || detectSystem();
  applyTheme(theme);

  // Listen to system changes (se user non ha override esplicito)
  if (typeof window !== 'undefined' && window.matchMedia) {
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => {
      // Solo se non c'è override esplicito
      if (!readStored()) {
        const sysTheme: Theme = mql.matches ? 'dark' : 'light';
        applyTheme(sysTheme);
        notify(sysTheme);
      }
    };
    if (mql.addEventListener) {
      mql.addEventListener('change', handler);
    } else if ((mql as MediaQueryList & { addListener?: (cb: () => void) => void }).addListener) {
      // Legacy Safari
      (mql as MediaQueryList & { addListener: (cb: () => void) => void }).addListener(handler);
    }
  }
}

// ============================================================
// Auto-bootstrap + window globals
// ============================================================

if (typeof window !== 'undefined') {
  window.tfTheme = {
    get: getTheme,
    set: setTheme,
    toggle: toggleTheme,
    init: initTheme,
  };

  // Init immediato (anti-FOUC: ANCHE se script defer, fallisce gracefully)
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initTheme);
  } else {
    initTheme();
  }
}
